import { HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SKIP_DEDUPE_HEADER } from '@core/constants';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

/**
 * The coalescing interceptor's memory: GETs in flight and recently answered GETs. It is a root service so there is
 * one per application, exactly as there was one instance of the class-based interceptor per application.
 */
@Injectable({ providedIn: 'root' })
export class RequestCoalescingStore {
    readonly inFlight = new Map<string, Observable<HttpEvent<unknown>>>();
    readonly cache = new Map<string, { expiry: number; response: HttpResponse<unknown> }>();
}

const DEFAULT_TTL_MS = 1000;

/**
 * Interceptor that coalesces duplicate in-flight GET requests and optionally caches
 * recent GET responses for a short TTL to prevent rapid sequential duplicates.
 *
 * - Only affects GET requests
 * - De-duplicates by `method + urlWithParams`
 * - Optional TTL via header `x-cache-ttl-ms`; defaults to 1000ms
 * - Skip coalescing/caching by setting header `x-skip-dedupe: true`
 */
export const requestCoalescingInterceptor: HttpInterceptorFn = (req, next) => {
    if (req.method !== 'GET' || shouldSkip(req)) {
        return next(req);
    }

    const { inFlight, cache } = inject(RequestCoalescingStore);
    const key = getKey(req);
    const now = Date.now();

    // Serve from short-lived cache if available
    const cached = cache.get(key);
    if (cached && cached.expiry > now) {
        return of(cached.response.clone());
    }

    // Coalesce in-flight duplicates
    const existing = inFlight.get(key);
    if (existing) {
        return existing;
    }

    const shared$ = next(req).pipe(
        tap(event => {
            if (event instanceof HttpResponse) {
                const ttl = resolveTtl(req);
                if (ttl > 0) {
                    // Expiry is measured from RESPONSE ARRIVAL, not request start. Using the
                    // request-start timestamp meant any request slower than the TTL (1s by default —
                    // easily exceeded by aggregate dashboard endpoints on hospital wifi) wrote a
                    // cache entry that was already expired, so the cache never hit on slow networks.
                    cache.set(key, { expiry: Date.now() + ttl, response: event });
                }
            }
        }),
        finalize(() => {
            inFlight.delete(key);
        }),
        shareReplay(1)
    );

    inFlight.set(key, shared$);
    return shared$;
};

function getKey(req: HttpRequest<unknown>): string {
    return `${req.method}::${req.urlWithParams}`;
}

function resolveTtl(req: HttpRequest<unknown>): number {
    const headerTtl = req.headers.get('x-cache-ttl-ms');
    const parsed = headerTtl ? Number(headerTtl) : NaN;
    return Number.isFinite(parsed) ? parsed : DEFAULT_TTL_MS;
}

function shouldSkip(req: HttpRequest<unknown>): boolean {
    return (req.headers.get(SKIP_DEDUPE_HEADER) || '').toLowerCase() === 'true';
}
